import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { profileAPI } from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { AppShell } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Search,
  MapPin,
  Star,
  ArrowRight,
  Building2,
  Wrench,
  Clock,
  Info,
} from 'lucide-react';

const COUNTRY_META = {
  DE: { flag: '\u{1F1E9}\u{1F1EA}', label: 'Deutschland' },
  SE: { flag: '\u{1F1F8}\u{1F1EA}', label: 'Schweden' },
  PL: { flag: '\u{1F1F5}\u{1F1F1}', label: 'Polen' },
  CZ: { flag: '\u{1F1E8}\u{1F1FF}', label: 'Tschechien' },
};

const SERVICES = [
  "Holzernte", "Rückung", "Aufforstung", "Waldpflege",
  "Harvesterarbeiten", "Forwarder", "Pflanzung", "Durchforstung"
];

const AnbieterPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [anbieter, setAnbieter] = useState([]);
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    country: searchParams.get('country') || '',
    service: searchParams.get('service') || '',
    search: ''
  });

  useEffect(() => {
    fetchAnbieter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.country, filters.service]);

  const fetchAnbieter = async () => {
    setLoading(true);
    try {
      const response = await profileAPI.listAnbieter({
        service: filters.service || undefined
      });
      setAnbieter(response.data);
    } catch (error) {
      console.error('Error fetching providers:', error);
      toast.error(t('toasts.anbieterLadeFehler'));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    const params = new URLSearchParams();
    if (newFilters.country) params.set('country', newFilters.country);
    if (newFilters.service) params.set('service', newFilters.service);
    setSearchParams(params);
  };

  const filteredAnbieter = anbieter.filter(a => {
    if (filters.country) {
      if ((a.country || 'DE') !== filters.country) return false;
    }
    if (filters.search) {
      const s = filters.search.toLowerCase();
      return (
        a.company_name?.toLowerCase().includes(s) ||
        a.user_name?.toLowerCase().includes(s) ||
        a.bundesland?.toLowerCase().includes(s) ||
        a.ort?.toLowerCase().includes(s)
      );
    }
    return true;
  });

  const renderStars = (rating) => (
    <div className="flex items-center gap-0.5" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-3.5 w-3.5 ${
            star <= Math.round(rating)
              ? 'fill-amber-400 text-amber-400'
              : 'text-gray-200'
          }`}
        />
      ))}
      <span className="ml-1 text-sm font-medium text-gray-700">{rating.toFixed(1)}</span>
    </div>
  );

  const countryCount = {};
  anbieter.forEach(a => {
    const c = a.country || 'DE';
    countryCount[c] = (countryCount[c] || 0) + 1;
  });

  const renderProviderCard = (provider) => {
    const country = provider.country || 'DE';
    const meta = COUNTRY_META[country] || COUNTRY_META.DE;
    return (
      <Link key={provider.id} to={`/anbieter/${provider.user_id}`} data-testid={`anbieter-card-${provider.id}`}>
        <Card className={`h-full hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group border-l-4 ${provider.is_demo ? 'border-l-transparent hover:border-l-gray-300' : 'border-l-primary/40 hover:border-l-primary'}`}>
          <CardContent className="p-5">
            <div className="flex items-start gap-3 mb-3">
              <div className="text-2xl leading-none pt-0.5 flex-shrink-0" data-testid="country-flag">{meta.flag}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors truncate text-[15px]">
                    {provider.company_name || provider.user_name}
                  </h3>
                  {provider.is_demo && (
                    <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-400 border border-gray-200 shrink-0" data-testid="demo-badge">Beispiel</span>
                  )}
                </div>
                <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                  <MapPin className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">{provider.ort}, {provider.bundesland}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 mb-3">
              {renderStars(provider.avg_rating || 0)}
              <span className="text-xs text-gray-400">({provider.review_count || 0})</span>
            </div>
            {provider.services?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {provider.services.slice(0, 3).map((service) => (
                  <Badge key={service} variant="secondary" className="bg-primary/8 text-primary text-xs font-normal">{service}</Badge>
                ))}
                {provider.services.length > 3 && (
                  <Badge variant="secondary" className="text-xs font-normal">+{provider.services.length - 3}</Badge>
                )}
              </div>
            )}
            <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
              {provider.experience_years && (
                <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{provider.experience_years} J. Erfahrung</span>
              )}
              {provider.service_radius_km && (
                <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{provider.service_radius_km} km Radius</span>
              )}
            </div>
            {provider.description && (
              <p className="text-xs text-gray-500 line-clamp-2 mb-3">{provider.description}</p>
            )}
            <Button variant="ghost" size="sm" className="w-full text-primary group-hover:bg-primary/5 text-xs">
              Profil ansehen <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          </CardContent>
        </Card>
      </Link>
    );
  };

  return (
    <AppShell>
      <div className="space-y-6" data-testid="anbieter-page">

        {/* Header */}
        <div>
          <h1 className="font-serif text-3xl font-bold text-gray-900" data-testid="anbieter-title">
            Forstdienstleister-Marktplatz
          </h1>
          <p className="text-gray-500 mt-1">
            Qualifizierte Anbieter aus Deutschland, Schweden, Polen und Tschechien
          </p>
        </div>

        {/* Info-Hinweis */}
        <div
          className="flex items-start gap-3 rounded-lg border border-warm-200 bg-warm-50 px-4 py-3"
          data-testid="anbieter-info-hint"
        >
          <Info className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
          <div className="text-sm text-gray-600">
            Dieser Marktplatz zeigt sowohl <strong>eingetragene Firmen</strong> als auch
            {' '}<strong>Beispielprofile</strong> auf Basis marktrelevanter Daten.
            Beispielprofile sind entsprechend gekennzeichnet.
          </div>
        </div>

        {/* Country Tabs */}
        <div className="flex flex-wrap gap-2" data-testid="country-tabs">
          <Button
            variant={!filters.country ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleFilterChange('country', '')}
            data-testid="filter-all"
          >
            Alle ({anbieter.length})
          </Button>
          {Object.entries(COUNTRY_META).map(([code, meta]) => {
            const count = countryCount[code] || 0;
            if (count === 0) return null;
            return (
              <Button
                key={code}
                variant={filters.country === code ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleFilterChange('country', code)}
                data-testid={`filter-${code.toLowerCase()}`}
              >
                <span className="mr-1.5">{meta.flag}</span>
                {meta.label} ({count})
              </Button>
            );
          })}
        </div>

        {/* Search + Service Filter */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={t('placeholders.firmaSuchen')}
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Select
                value={filters.service || 'all'}
                onValueChange={(v) => handleFilterChange('service', v === 'all' ? '' : v)}
              >
                <SelectTrigger className="w-full md:w-52" data-testid="service-filter">
                  <SelectValue placeholder={t('placeholders.dienstleistung')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Dienstleistungen</SelectItem>
                  {SERVICES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <p className="text-sm text-gray-500" data-testid="result-count">
          {filteredAnbieter.length} Anbieter gefunden
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-pulse text-primary">Laden...</div>
          </div>
        ) : filteredAnbieter.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Wrench className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Keine Anbieter gefunden</h3>
              <p className="text-gray-500">Versuchen Sie andere Filter oder Suchbegriffe</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {/* Eingetragene Firmen zuerst */}
            {filteredAnbieter.filter(p => !p.is_demo).length > 0 && (
              <>
                <div className="md:col-span-2 lg:col-span-3">
                  <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide flex items-center gap-2" data-testid="section-real">
                    <Building2 className="h-4 w-4 text-primary" />
                    Eingetragene Firmen
                  </h2>
                </div>
                {filteredAnbieter.filter(p => !p.is_demo).map((provider) => renderProviderCard(provider))}
              </>
            )}

            {/* Beispielprofile */}
            {filteredAnbieter.filter(p => p.is_demo).length > 0 && (
              <>
                <div className="md:col-span-2 lg:col-span-3 mt-4">
                  <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2" data-testid="section-demo">
                    <Info className="h-4 w-4 text-muted-foreground" />
                    Beispielprofile
                    <span className="text-xs font-normal normal-case text-muted-foreground/70">
                      &ndash; Illustrative Darstellungen auf Basis marktrelevanter Daten
                    </span>
                  </h2>
                </div>
                {filteredAnbieter.filter(p => p.is_demo).map((provider) => renderProviderCard(provider))}
              </>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
};

export default AnbieterPage;
